// API helper para el panel CRM unificado.
// Tablas: conversations (multi-canal), messages, customer_events.

import { supabase } from './supabase'

/**
 * Lista de conversaciones con filtros + agregación de cliente.
 * filtros: { canal?: 'whatsapp'|'instagram'|'email'|'webchat'|'todos', estado?: 'abierta'|'cerrada'|'pendiente'|'todos', soloNoLeidos?: boolean, segmento?: 'suscriptor'|'visitante'|'b2b'|'todos', q?: string }
 */
export async function getConversaciones({ canal = 'todos', estado = 'todos', soloNoLeidos = false, segmento = 'todos', q = '' } = {}) {
  let query = supabase
    .from('conversations')
    .select(`
      id, canal, phone, ig_username, nombre_visible, cliente_id, contacto_id,
      foto_perfil_url, foto_perfil_source,
      unread_count, last_message_at, last_message_preview, last_message_from,
      agente_asignado, estado, tags, ventana_24h_hasta, created_at,
      cliente:clientes!conversations_cliente_id_fkey(id, alias, barrio, kit_actual, estado),
      agente:profiles!conversations_agente_asignado_fkey(id, nombre),
      contacto:crm_contactos!conversations_contacto_id_fkey(id, tipos, empresa)
    `)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .limit(100)

  if (canal !== 'todos') query = query.eq('canal', canal)
  if (estado !== 'todos') query = query.eq('estado', estado)
  if (soloNoLeidos) query = query.gt('unread_count', 0)

  // Búsqueda: nombre/teléfono/IG/last_message + fallback en mensajes históricos
  const termRaw = q.trim()
  if (termRaw) {
    const term = `%${termRaw}%`
    query = query.or(
      `nombre_visible.ilike.${term},phone.ilike.${term},ig_username.ilike.${term},last_message_preview.ilike.${term}`
    )
  }

  const { data, error } = await query
  if (error) { console.error('getConversaciones', error); return [] }

  // Si la búsqueda no devolvió mucho y tiene 3+ chars, buscar también en mensajes históricos
  // y mergear conversaciones que matcheen por contenido pasado.
  let extras = []
  if (termRaw && termRaw.length >= 3 && (data?.length || 0) < 20) {
    const { data: msgMatches } = await supabase
      .from('messages')
      .select('conversation_id')
      .ilike('texto', `%${termRaw}%`)
      .order('created_at', { ascending: false })
      .limit(40)
    const idsYaIncluidos = new Set((data || []).map(c => c.id))
    const idsExtra = [...new Set((msgMatches || []).map(m => m.conversation_id))]
      .filter(id => !idsYaIncluidos.has(id))
    if (idsExtra.length > 0) {
      const { data: convExtra } = await supabase
        .from('conversations')
        .select(`
          id, canal, phone, ig_username, nombre_visible, cliente_id,
          unread_count, last_message_at, last_message_preview, last_message_from,
          agente_asignado, estado, tags, ventana_24h_hasta, created_at,
          cliente:clientes!conversations_cliente_id_fkey(id, alias, barrio, kit_actual, estado),
          agente:profiles!conversations_agente_asignado_fkey(id, nombre)
        `)
        .in('id', idsExtra)
        .limit(20)
      extras = (convExtra || []).map(c => ({ ...c, _matchHistorico: true }))
    }
  }

  // Filtro por segmento — usa crm_contactos.tipos (rolodex unificado)
  let lista = [...(data || []), ...extras]
  if (segmento !== 'todos') {
    lista = lista.filter(c => {
      const tipos = c.contacto?.tipos || []
      if (segmento === 'sin_clasificar') return tipos.length === 0
      // cliente / proveedor / interno / prospecto / otro
      return tipos.includes(segmento)
    })
  }

  // Sumar auto-tags derivados del cliente (un solo bulk call)
  const clienteIds = [...new Set(lista.map(c => c.cliente_id).filter(Boolean))]
  if (clienteIds.length > 0) {
    const { data: autoData } = await supabase.rpc('crm_get_clientes_auto_tags', { p_ids: clienteIds })
    const autoMap = Object.fromEntries((autoData || []).map(r => [r.cliente_id, r.auto_tags || []]))
    lista = lista.map(c => ({ ...c, auto_tags: c.cliente_id ? (autoMap[c.cliente_id] || []) : [] }))
  } else {
    lista = lista.map(c => ({ ...c, auto_tags: [] }))
  }
  return lista
}

/** Mensajes de una conversación, orden cronológico ascendente. */
export async function getMensajes(conversationId, { limit = 200 } = {}) {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      id, external_message_id, direccion, tipo, texto,
      media_url, media_mime, media_filename,
      template_name, template_lang, template_params,
      agente_id, estado, estado_error,
      reaction, reaction_from, reaction_at,
      origen, automatizacion_id,
      created_at, sent_at, delivered_at, read_at,
      agente:profiles!messages_agente_id_fkey(id, nombre)
    `)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(limit)
  if (error) { console.error('getMensajes', error); return [] }
  return data || []
}

/** Timeline 360 del cliente (mensajes + eventos del sistema). */
export async function getTimelineCliente(clienteId, { limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('customer_events')
    .select('id, tipo, titulo, descripcion, metadata, importancia, conversation_id, created_at')
    .eq('cliente_id', clienteId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) { console.error('getTimelineCliente', error); return [] }
  return data || []
}

/** Ficha resumida del cliente para el panel lateral + KPIs. */
export async function getFichaCliente(clienteId) {
  const [resCliente, resKpis] = await Promise.all([
    supabase
      .from('clientes')
      .select(`
        id, alias, barrio, direccion, dia_entrega, ventana_horaria,
        kit_actual, estado, fecha_proximo_cobro, mp_card_last4, mp_card_brand,
        kit:kits!clientes_kit_actual_fkey(codigo, nombre, precio_susc, tier),
        titular:profiles!clientes_titular_fkey(id, nombre, phone, email)
      `)
      .eq('id', clienteId)
      .single(),
    supabase.rpc('crm_ficha_cliente_kpis', { p_cliente_id: clienteId }),
  ])
  if (resCliente.error) { console.error('getFichaCliente', resCliente.error); return null }
  return { ...resCliente.data, kpis: resKpis.data || null }
}

/** Marca conversación como leída (reset unread_count). */
export async function marcarComoLeida(conversationId) {
  const { error } = await supabase
    .from('conversations')
    .update({ unread_count: 0 })
    .eq('id', conversationId)
  if (error) console.error('marcarComoLeida', error)
}

/** Cambia estado de conversación. */
export async function cambiarEstadoConversacion(conversationId, estado) {
  const { error } = await supabase
    .from('conversations')
    .update({ estado })
    .eq('id', conversationId)
  if (error) throw error
}

/** Asigna agente a la conversación. */
export async function asignarAgente(conversationId, agenteId) {
  const { error } = await supabase
    .from('conversations')
    .update({ agente_asignado: agenteId })
    .eq('id', conversationId)
  if (error) throw error
}

/** Notas internas. */
export async function guardarNotasInternas(conversationId, notas) {
  const { error } = await supabase
    .from('conversations')
    .update({ notas_internas: notas })
    .eq('id', conversationId)
  if (error) throw error
}

/** Tags. */
export async function actualizarTags(conversationId, tags) {
  const { error } = await supabase
    .from('conversations')
    .update({ tags })
    .eq('id', conversationId)
  if (error) throw error
}

/* ─────────── Iniciar conversación proactiva (templates) ─────────── */

/** Devuelve templates APROBADOS de la WABA activa, ordenados alfabético. */
export async function getTemplatesAprobados() {
  const { data, error } = await supabase.functions.invoke('wa-list-templates', { body: {} })
  if (error) { console.error('getTemplatesAprobados', error); return [] }
  const tpls = (data?.templates || []).filter(t => t.status === 'APPROVED')
  // Parsear: extraer el body text + cantidad de variables
  return tpls.map(t => {
    const bodyComp = (t.components || []).find(c => c.type === 'BODY')
    const text = bodyComp?.text || ''
    const variables = [...text.matchAll(/\{\{(\d+)\}\}/g)].map(m => parseInt(m[1], 10))
    const maxVar = variables.length ? Math.max(...variables) : 0
    return {
      name: t.name,
      language: t.language,
      category: t.category,
      body: text,
      variables_count: maxVar,
    }
  }).sort((a, b) => a.name.localeCompare(b.name))
}

/** Búsqueda rápida de clientes para el modal de nueva conversación. */
export async function buscarClientesParaCRM(q = '') {
  const term = q.trim()
  let query = supabase
    .from('clientes')
    .select(`
      id, alias, barrio, kit_actual, estado,
      titular:profiles!clientes_titular_fkey(id, nombre, phone)
    `)
    .order('created_at', { ascending: false })
    .limit(30)
  // Filtro client-side después (joins en or es feo en supabase)
  const { data, error } = await query
  if (error) { console.error('buscarClientesParaCRM', error); return [] }
  if (!term) return data || []
  const t = term.toLowerCase()
  return (data || []).filter(c =>
    (c.alias || '').toLowerCase().includes(t) ||
    (c.titular?.nombre || '').toLowerCase().includes(t) ||
    (c.titular?.phone || '').includes(term)
  )
}

/**
 * Vincula una conversación con un cliente existente.
 * Útil cuando un visitante anónimo (sin cliente_id) es en realidad
 * un cliente nuestro y queremos asociar el chat manualmente.
 */
export async function vincularConversacionACliente(convId, clienteId) {
  const { error } = await supabase
    .from('conversations')
    .update({ cliente_id: clienteId })
    .eq('id', convId)
  if (error) throw new Error('No se pudo vincular: ' + error.message)
  return true
}

/** Desvincula una conversación del cliente (la deja como visitante). */
export async function desvincularConversacionDeCliente(convId) {
  const { error } = await supabase
    .from('conversations')
    .update({ cliente_id: null })
    .eq('id', convId)
  if (error) throw new Error('No se pudo desvincular: ' + error.message)
  return true
}

/** Aplica reemplazos de {{1}}, {{2}}, etc al body del template. */
export function aplicarTemplateVars(body, params) {
  if (!body) return ''
  return body.replace(/\{\{(\d+)\}\}/g, (_, idx) => {
    const i = parseInt(idx, 10) - 1
    return params?.[i] ?? `{{${idx}}}`
  })
}

/**
 * Crea (o reusa) conversación con un phone y manda template via Meta.
 * Si el cliente ya tiene conv abierta, la reusa.
 */
export async function iniciarConversacionConTemplate({ phone, nombreVisible, clienteId, templateName, templateLang, templateParams }) {
  // Normalizar phone (similar al backend)
  let p = String(phone).replace(/[^0-9]/g, '')
  if (p.startsWith('0') && p.length === 9) p = '598' + p.slice(1)
  if (p.length === 8 && p.startsWith('9')) p = '598' + p
  if (p.length < 10) throw new Error('Teléfono inválido (mínimo 9 dígitos)')

  // Upsert conversation
  const { data: existing } = await supabase
    .from('conversations')
    .select('id, cliente_id, nombre_visible')
    .eq('canal', 'whatsapp')
    .eq('phone', p)
    .maybeSingle()

  let conversationId
  if (existing) {
    conversationId = existing.id
    // Actualizar nombre/cliente si se pasó info nueva
    const updates = {}
    if (nombreVisible && existing.nombre_visible !== nombreVisible) updates.nombre_visible = nombreVisible
    if (clienteId && existing.cliente_id !== clienteId) updates.cliente_id = clienteId
    if (Object.keys(updates).length > 0) {
      await supabase.from('conversations').update(updates).eq('id', conversationId)
    }
  } else {
    const { data: created, error: errIns } = await supabase
      .from('conversations')
      .insert({
        canal: 'whatsapp',
        phone: p,
        nombre_visible: nombreVisible || null,
        cliente_id: clienteId || null,
        estado: 'abierta',
      })
      .select('id')
      .single()
    if (errIns) throw new Error('No se pudo crear conversación: ' + errIns.message)
    conversationId = created.id
  }

  // Enviar template vía wa-reply
  const { data, error } = await supabase.functions.invoke('wa-reply', {
    body: {
      conversation_id: conversationId,
      tipo: 'template',
      template: {
        name: templateName,
        lang: templateLang || 'es_UY',
        params: templateParams || [],
      },
    },
  })
  if (error) throw new Error(error.message || 'Error al enviar template')
  if (data?.error) throw new Error(data.error + (data.detail ? ': ' + data.detail : ''))

  return { conversationId, ...data }
}

/** Pide sugerencia de respuesta a Claude Haiku para la conversación. */
export async function sugerirRespuesta(conversationId) {
  const { data, error } = await supabase.functions.invoke('crm-sugerir-respuesta', {
    body: { conversation_id: conversationId },
  })
  if (error) throw new Error(error.message || 'Error al sugerir')
  if (data?.error) throw new Error(data.error)
  return data // { sugerencia, confianza, etiqueta, modelo }
}

/** Equipo asignable: admin + repartidor. Cacheado en memoria simple. */
let _equipoCache = null
export async function getEquipo() {
  if (_equipoCache) return _equipoCache
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nombre, role')
    .in('role', ['admin', 'repartidor'])
    .order('nombre')
  if (error) { console.error('getEquipo', error); return [] }
  _equipoCache = data || []
  return _equipoCache
}

/**
 * Enviar mensaje outbound vía wa-reply.
 * @param {object} opts { conversation_id, tipo, texto, media_url, media_mime, media_filename, template, location }
 */
export async function enviarMensaje(opts) {
  // Detectar canal: necesitamos pasarlo o consultarlo. Si no viene, asumimos whatsapp por compat.
  // Pero lo correcto es que el componente nos diga el canal directamente.
  let canal = opts.canal
  if (!canal && opts.conversation_id) {
    const { data: conv } = await supabase
      .from('conversations')
      .select('canal')
      .eq('id', opts.conversation_id)
      .single()
    canal = conv?.canal || 'whatsapp'
  }
  // Sacamos canal del payload (no lo entiende la edge)
  const { canal: _canal, ...payload } = opts

  const edge = canal === 'instagram' ? 'ig-reply' : 'wa-reply'
  const { data, error } = await supabase.functions.invoke(edge, { body: payload })

  // Cuando la edge retorna HTTP error (4xx/5xx), el SDK pone FunctionsHttpError en `error` pero NO
  // parsea el body. Sin esto el usuario solo ve "non-2xx status code" sin saber el motivo real.
  // Intentamos leer el body para mostrar el mensaje verdadero (meta api error, ventana cerrada, etc).
  if (error) {
    let detalle = error.message || 'Error al enviar'
    let esTransitorio = false
    try {
      const ctx = error.context
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json()
        if (body?.error) detalle = body.error + (body.detail ? ' — ' + body.detail : '')
        else if (body?.message) detalle = body.message
        // Meta marca errores temporales con is_transient=true. Detectarlo da mejor UX.
        const metaErr = body?.raw?.error
        if (metaErr?.is_transient || metaErr?.code === 2) {
          esTransitorio = true
          detalle = 'Meta tiene un hipo temporal — esperá 1-2 minutos y reintentá. ' + (metaErr.message || '')
        }
      } else if (ctx && typeof ctx.text === 'function') {
        const text = await ctx.text()
        if (text) detalle = text.slice(0, 300)
      }
    } catch { /* body no parseable o ya consumido — usamos detalle anterior */ }
    const err = new Error(detalle)
    err.esTransitorio = esTransitorio
    throw err
  }
  if (data?.error) throw new Error(data.error + (data.detail ? ' — ' + data.detail : ''))
  return data
}

/**
 * Sube un archivo al bucket crm-media y devuelve { url, mime, filename, tipoWA }.
 * tipoWA: 'image' | 'document' | 'audio' | 'video' segun el mime.
 * Limites: 16MB (audio/video), 5MB (imagen), 100MB (doc) — Meta. Bucket cap 16MB.
 */
export async function subirAdjuntoCRM(file, conversationId) {
  if (!file) throw new Error('Sin archivo')
  if (file.size > 16 * 1024 * 1024) throw new Error('Máx 16 MB')

  const mime = file.type || 'application/octet-stream'
  let tipoWA = 'document'
  if (mime.startsWith('image/')) tipoWA = 'image'
  else if (mime.startsWith('audio/')) tipoWA = 'audio'
  else if (mime.startsWith('video/')) tipoWA = 'video'

  // Limites Meta
  if (tipoWA === 'image' && file.size > 5 * 1024 * 1024) throw new Error('Imagen máx 5 MB')

  const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
  const safe = file.name.replace(/[^a-z0-9._-]/gi, '_').slice(0, 60)
  const path = `${conversationId || 'misc'}/${Date.now()}_${safe}`

  const { error: upErr } = await supabase.storage
    .from('crm-media')
    .upload(path, file, { contentType: mime, upsert: false })
  if (upErr) throw new Error('Upload: ' + upErr.message)

  const { data: pub } = supabase.storage.from('crm-media').getPublicUrl(path)
  if (!pub?.publicUrl) throw new Error('No se pudo obtener URL pública')

  return {
    url: pub.publicUrl,
    mime,
    filename: file.name,
    tipoWA,
  }
}

// ──────────── AVATARES ────────────

/** Sube una foto de perfil para una conversación. Bucket público crm-avatars. */
export async function subirAvatarConversacion(file, conversationId) {
  if (!file) throw new Error('Sin archivo')
  if (!file.type?.startsWith('image/')) throw new Error('Tiene que ser una imagen')
  if (file.size > 5 * 1024 * 1024) throw new Error('Máx 5 MB')

  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
  const path = `${conversationId}/${Date.now()}.${ext}`
  const { error: upErr } = await supabase.storage
    .from('crm-avatars')
    .upload(path, file, { contentType: file.type, upsert: false })
  if (upErr) throw new Error('Upload: ' + upErr.message)

  const { data: pub } = supabase.storage.from('crm-avatars').getPublicUrl(path)
  if (!pub?.publicUrl) throw new Error('No se pudo obtener URL')

  // Setear en conversation
  const { error: updErr } = await supabase
    .from('conversations')
    .update({ foto_perfil_url: pub.publicUrl, foto_perfil_source: 'manual' })
    .eq('id', conversationId)
  if (updErr) throw new Error('Actualizar conversación: ' + updErr.message)

  return pub.publicUrl
}

/** Usa la URL de un mensaje image como avatar (un click desde la burbuja). */
export async function usarMensajeComoAvatar(conversationId, mensajeMediaUrl) {
  if (!mensajeMediaUrl) throw new Error('Sin URL')
  const { error } = await supabase
    .from('conversations')
    .update({ foto_perfil_url: mensajeMediaUrl, foto_perfil_source: 'manual' })
    .eq('id', conversationId)
  if (error) throw error
}

/** Reaccionar a un mensaje desde el CRM (operador → cliente). */
export async function reaccionarMensaje({ conversationId, canal, messageId, emoji }) {
  return enviarMensaje({
    conversation_id: conversationId,
    canal,
    tipo: 'reaction',
    message_id_target: messageId,
    emoji,
  })
}

/** Quitar reacción del agente sobre un mensaje. */
export async function quitarReaccionMensaje({ conversationId, canal, messageId }) {
  return enviarMensaje({
    conversation_id: conversationId,
    canal,
    tipo: 'unreact',
    message_id_target: messageId,
  })
}

/** Quitar foto (vuelve al avatar generativo). */
export async function quitarAvatar(conversationId) {
  const { error } = await supabase
    .from('conversations')
    .update({ foto_perfil_url: null, foto_perfil_source: 'auto' })
    .eq('id', conversationId)
  if (error) throw error
}

/**
 * Suscribirse a cambios en tiempo real de una conversación.
 * Devuelve la suscripción para que el caller la desconecte cuando desmonta.
 */
export function suscribirMensajesEnVivo(conversationId, onChange) {
  const channel = supabase
    .channel(`crm:messages:${conversationId}`)
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'messages', filter: `conversation_id=eq.${conversationId}` },
      payload => onChange(payload))
    .subscribe()
  return channel
}

/** Suscribirse a cambios globales en conversaciones (para refrescar la lista del inbox). */
export function suscribirInboxEnVivo(onChange) {
  const channel = supabase
    .channel('crm:conversations')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'conversations' },
      payload => onChange(payload))
    .subscribe()
  return channel
}

/* ─────────── Respuestas rápidas (canned responses) ─────────── */

export async function getRespuestasRapidas() {
  const { data, error } = await supabase
    .from('respuestas_rapidas')
    .select('id, slug, titulo, texto, categoria, uso_count, orden')
    .eq('activa', true)
    .order('uso_count', { ascending: false })
    .order('orden', { ascending: true })
  if (error) { console.error('getRespuestasRapidas', error); return [] }
  return data || []
}

export async function crearRespuestaRapida(payload) {
  const { data, error } = await supabase
    .from('respuestas_rapidas')
    .insert(payload)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function actualizarRespuestaRapida(id, payload) {
  const { error } = await supabase
    .from('respuestas_rapidas')
    .update(payload)
    .eq('id', id)
  if (error) throw error
}

export async function eliminarRespuestaRapida(id) {
  const { error } = await supabase
    .from('respuestas_rapidas')
    .update({ activa: false })
    .eq('id', id)
  if (error) throw error
}

export async function incrementarUsoRespuesta(id) {
  // Incrementa atómicamente uso_count via RPC simple
  await supabase.rpc('incrementar_uso_respuesta', { rr_id: id }).then(({ error }) => {
    // Si no existe el RPC, hacemos fallback con UPDATE de una sola fila
    if (error) {
      supabase.from('respuestas_rapidas').select('uso_count').eq('id', id).single().then(({ data }) => {
        if (data) supabase.from('respuestas_rapidas').update({ uso_count: (data.uso_count || 0) + 1 }).eq('id', id)
      })
    }
  })
}

/**
 * Reemplaza variables {nombre} {kit} {barrio} {dia_entrega} etc. con datos del cliente.
 * @param {string} texto — template con variables
 * @param {object} ctx — { conv, ficha } (conversation + ficha cliente del CRM)
 */
export function aplicarVariables(texto, ctx) {
  if (!texto) return ''
  const { conv, ficha } = ctx || {}
  const VENTANAS = { manana: 'la mañana (8-12 hs)', tarde: 'la tarde (14-18 hs)' }
  const reemplazos = {
    nombre: conv?.nombre_visible?.split(' ')[0] || ficha?.titular?.nombre?.split(' ')[0] || 'amig@',
    nombre_completo: conv?.nombre_visible || ficha?.titular?.nombre || '',
    kit: ficha?.kit?.nombre || 'tu kit',
    barrio: ficha?.barrio || conv?.cliente?.barrio || 'tu barrio',
    direccion: ficha?.direccion || '',
    dia_entrega: VENTANAS[ficha?.ventana_horaria] || ficha?.dia_entrega || 'tu horario',
    proxima_entrega: ficha?.fecha_proximo_cobro
      ? new Date(ficha.fecha_proximo_cobro).toLocaleDateString('es-UY', { day: 'numeric', month: 'long' })
      : 'tu próxima fecha',
    cuota: ficha?.kit?.precio_susc ? `$${ficha.kit.precio_susc.toLocaleString('es-UY')}` : '—',
    ultimo_cobro: ficha?.mp_card_last4 ? `Visa •••${ficha.mp_card_last4}` : '—',
    tarjeta: ficha?.mp_card_brand && ficha?.mp_card_last4
      ? `${ficha.mp_card_brand} •••${ficha.mp_card_last4}`
      : '—',
    telefono: conv?.phone ? '+' + conv.phone : '',
  }
  return texto.replace(/\{(\w+)\}/g, (_, key) => reemplazos[key] ?? `{${key}}`)
}

// ════════════════════════════════════════════════════════════════════
// CRM Contactos (rolodex unificado: cliente / proveedor / interno / prospecto / otro)
// ════════════════════════════════════════════════════════════════════

export const TIPOS_CONTACTO = [
  { v: 'cliente',    l: 'Cliente Limpack', icon: '🛒', color: '#16A34A' },
  { v: 'proveedor',  l: 'Proveedor',       icon: '📦', color: '#7C3AED' },
  { v: 'interno',    l: 'Interno Limpack', icon: '👤', color: '#0F766E' },
  { v: 'prospecto',  l: 'Prospecto',       icon: '✨', color: '#D97706' },
  { v: 'otro',       l: 'Otro',            icon: '📌', color: '#64748B' },
]

/** Trae el contacto vinculado a una conversación. */
export async function getContactoDeConversacion(conversationId) {
  const { data: conv, error: e1 } = await supabase
    .from('conversations').select('contacto_id').eq('id', conversationId).single()
  if (e1) { console.error(e1); return null }
  if (!conv?.contacto_id) return null
  const { data, error } = await supabase
    .from('crm_contactos')
    .select('id, nombre, telefono_e164, ig_user_id, ig_username, email, tipos, cliente_id, empresa, notas, created_at, updated_at')
    .eq('id', conv.contacto_id).single()
  if (error) { console.error(error); return null }
  return data
}

/** Actualiza tipos/empresa/notas/nombre de un contacto. */
export async function actualizarContacto(contactoId, patch) {
  const fields = {}
  if (patch.tipos !== undefined) fields.tipos = patch.tipos
  if (patch.empresa !== undefined) fields.empresa = patch.empresa
  if (patch.notas !== undefined) fields.notas = patch.notas
  if (patch.nombre !== undefined) fields.nombre = patch.nombre
  if (patch.email !== undefined) fields.email = patch.email
  if (patch.cliente_id !== undefined) fields.cliente_id = patch.cliente_id
  const { data, error } = await supabase
    .from('crm_contactos').update(fields).eq('id', contactoId).select().single()
  if (error) throw new Error('No se pudo actualizar contacto: ' + error.message)
  return data
}

/** Lista del rolodex con filtros. */
export async function listarContactos({ tipo = null, empresa = null, q = '', limit = 200 } = {}) {
  let query = supabase
    .from('crm_contactos')
    .select('id, nombre, telefono_e164, ig_username, email, tipos, cliente_id, empresa, notas, created_at')
    .order('updated_at', { ascending: false })
    .limit(limit)
  if (tipo === 'sin_clasificar') query = query.eq('tipos', '{}')
  else if (tipo) query = query.contains('tipos', [tipo])
  if (empresa) query = query.ilike('empresa', `%${empresa}%`)
  if (q && q.trim()) {
    const t = `%${q.trim()}%`
    query = query.or(`nombre.ilike.${t},telefono_e164.ilike.${t},ig_username.ilike.${t},email.ilike.${t},empresa.ilike.${t}`)
  }
  const { data, error } = await query
  if (error) { console.error(error); return [] }
  return data || []
}

/** Crea contacto manual desde el rolodex. */
export async function crearContactoManual({ nombre, telefono, ig_username, email, tipos = [], empresa = null, notas = null }) {
  const fields = {
    nombre: nombre || null,
    telefono_e164: telefono || null,
    ig_username: ig_username || null,
    email: email || null,
    tipos,
    empresa,
    notas,
  }
  const { data, error } = await supabase.from('crm_contactos').insert(fields).select().single()
  if (error) throw new Error('No se pudo crear contacto: ' + error.message)
  return data
}

/** Suma o saca un tipo del array. */
export async function toggleTipoContacto(contactoId, tipo) {
  const { data: actual, error: e1 } = await supabase
    .from('crm_contactos').select('tipos, cliente_id').eq('id', contactoId).single()
  if (e1) throw new Error(e1.message)
  const tienenTipo = (actual?.tipos || []).includes(tipo)
  const nuevosTipos = tienenTipo
    ? actual.tipos.filter(t => t !== tipo)
    : [...(actual?.tipos || []), tipo]
  // Si saca 'cliente' debe limpiar cliente_id (sino el CHECK falla)
  const payload = { tipos: nuevosTipos }
  if (tipo === 'cliente' && tienenTipo) payload.cliente_id = null
  const { data, error } = await supabase
    .from('crm_contactos').update(payload).eq('id', contactoId).select().single()
  if (error) throw new Error(error.message)
  return data
}
